/**
 * The recorder's mic-level bars (design meetings-v2 `.lvl`): how loud the microphone is, for display only.
 *
 * An AnalyserNode reads the mic stream's loudness; nothing is recorded, kept or sent. The recogniser (session.ts)
 * opens the microphone itself; this only reads a level from it. If the level cannot be read (no permission yet, no
 * Web Audio), the bars stay flat and voice typing is unaffected.
 */
import { useEffect, useState } from 'react';

const BARS = 28;
const FLAT = Array.from({ length: BARS }, () => 0);

/** 28 values 0..1 while `on`, about 15 times a second; all 0 when off. */
export function useMicLevel(on: boolean): number[] {
  const [lv, setLv] = useState<number[]>(FLAT);
  useEffect(() => {
    if (!on) {
      setLv(FLAT);
      return;
    }
    return openMicLevel(setLv);
  }, [on]);
  return lv;
}

/**
 * Opens the microphone and reports its level to `onLevel` until the returned close function is called. Closing
 * always releases the microphone: also when it is called before getUserMedia resolves (the hook switched off while
 * the permission prompt was up), in which case the stream is stopped the moment it arrives (finding sf-b76d252b).
 */
export function openMicLevel(onLevel: (lv: number[]) => void): () => void {
  let stop = false;
  let stream: MediaStream | null = null;
  let ctx: AudioContext | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  const history: number[] = [...FLAT];
  const release = (s: MediaStream) => s.getTracks().forEach((t) => t.stop());
  void (async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof AudioContext === 'undefined') return;
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (stop) {
        release(s); // closed while the prompt was up: nobody else will stop this stream
        return;
      }
      stream = s;
      ctx = new AudioContext();
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(s).connect(an);
      const buf = new Uint8Array(an.fftSize);
      timer = setInterval(() => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += ((v - 128) / 128) ** 2;
        const rms = Math.min(1, Math.sqrt(sum / buf.length) * 4);
        history.shift();
        history.push(rms);
        onLevel([...history]);
      }, 66);
    } catch {
      /* no level: the bars stay flat (close() still releases a stream that was opened) */
    }
  })();
  return () => {
    stop = true;
    if (timer !== null) clearInterval(timer);
    if (stream) release(stream);
    void ctx?.close().catch(() => undefined);
  };
}
